/**
 * Lo que los dos compositores de Sol comparten para adjuntar: las imágenes, la
 * nota de voz y el texto de carga del mensaje en vuelo.
 */
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import type { CitacionesDeUsuario, SendMessageRequest } from '@/services/ragChatService';

import { AVISO_NOTA_FALLIDA, prepararAudio, textoDeCarga } from '../lib/adjuntos';
import { useImagenesAdjuntas } from './useImagenesAdjuntas';

export interface NotaDeVoz {
  blob: Blob;
  duracionMs: number;
}

export function useAdjuntosDeSol({ bloqueado = false }: { bloqueado?: boolean } = {}) {
  const imagenes = useImagenesAdjuntas({ bloqueado });
  const { paraEnviar, idsParaEnviar, esperarProcesado } = imagenes;
  const [carga, setCarga] = useState<{
    imagenes: boolean;
    audio: boolean;
  } | null>(null);

  /**
   * Arma `images` / `audio` del pedido y deja listo el texto de carga. Con nota
   * de voz espera a que terminen de reducirse las imágenes en curso, para que no
   * se pierda ni la nota ni esas imágenes. Si falla avisa y devuelve null.
   */
  const armar = useCallback(
    async (nota?: NotaDeVoz) => {
      try {
        if (nota) await esperarProcesado();
        const images = paraEnviar();
        const ids = idsParaEnviar();
        const audio = nota ? await prepararAudio(nota.blob, nota.duracionMs) : undefined;
        setCarga({ imagenes: images.length > 0, audio: !!audio });
        const citations: CitacionesDeUsuario = {};
        if (images.length > 0) {
          citations.imagenes = {
            cantidad: images.length,
            descripciones: images.map(() => null),
            sin_ver: 0,
          };
        }
        if (audio) citations.audio = { duration_ms: audio.duration_ms, transcripto: false };
        const extra: Pick<SendMessageRequest, 'images' | 'audio'> = {};
        if (images.length > 0) extra.images = images;
        if (audio) extra.audio = audio;
        return {
          extra,
          ids,
          citations: images.length > 0 || audio ? citations : null,
        };
      } catch {
        toast.error(nota ? AVISO_NOTA_FALLIDA : 'No se pudieron preparar los adjuntos.');
        return null;
      }
    },
    [paraEnviar, idsParaEnviar, esperarProcesado],
  );

  /** Aviso de un envío que falló: con nota de voz dice que hay que grabarla de nuevo. */
  const avisarFallo = useCallback((nota: NotaDeVoz | undefined, titulo: string, detalle: string) => {
    if (nota) toast.error(AVISO_NOTA_FALLIDA, { description: detalle });
    else toast.error(titulo, { description: detalle });
  }, []);

  const terminar = useCallback(() => setCarga(null), []);

  return { imagenes, armar, avisarFallo, terminar, textoDeCarga: textoDeCarga(carga) };
}
